import { Injector } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { Logger } from 'common/log'
import { MediaFile, MediaFileBundle } from 'elements-sdk'
import { ElementsClientService } from 'common/util.elementsClient/module'
import { MessageBoxService } from 'common/util/module'
import { NotifyService } from 'common/util/services/notify.service'
import { ClientSideURLService } from 'media/services/clientSideURL.service'

export async function loadAssetContentsPages (
  api: APIService,
  dir: MediaFile,
  callback: (bundles: MediaFileBundle[], files: MediaFile[]) => Promise<any>,
) {
  let offset = 0
  let total = 1

  while (offset < total) {
    const contents = await api.mediaLibrary.getMediaFileContents({
      id: dir.id,
      offset,
      limit: 50,
      includeModifiedBy: true,
      excludeUnrecognized: true,
      excludeDeleted: true,
    })
    offset += 50
    total = contents.total

    const bundles = contents.bundles.filter(x => {
      return x.asset &&
      ['video', 'audio', 'image'].includes(x.asset.type!) &&
      x.mainfile.present
    })

    await callback(bundles, contents.directories)
  }
}

export abstract class BaseInterface {
  logger = new Logger(__filename)
  uri = true
  native = false
  active = false
  platform: string
  app: string
  currentMediaRootID: number|undefined
  systemPath: Record<string, string>
  protected api: APIService
  protected clientSideUrl: ClientSideURLService
  protected notify: NotifyService
  protected elementsClient: ElementsClientService
  protected translate: TranslateService
  protected messageBox: MessageBoxService

  constructor (
    protected injector: Injector,
  ) {
    this.api = injector.get(APIService)
    this.clientSideUrl = injector.get(ClientSideURLService)
    this.notify = injector.get(NotifyService)
    this.elementsClient = injector.get(ElementsClientService)
    this.translate = injector.get(TranslateService)
    this.messageBox = injector.get(MessageBoxService)
  }

  abstract init (): Promise<void>

  abstract getFS (): any

  abstract getSystemPath (name: string): string

  abstract getApp (): string

  abstract getPlatform (): string

  abstract restart ()

  abstract getActiveSequencePaths (): Promise<string[]>

  abstract getProjectPaths (): Promise<string[]>

  abstract isProxyMode (): Promise<boolean>

  // eslint-disable-next-line no-unused-vars
  abstract setProxyMode (enabled)

  // eslint-disable-next-line no-unused-vars
  abstract attachProxies (paths: Record<string, string>)

  abstract importDirs (dirs, folder?): Promise<void>

  // eslint-disable-next-line no-unused-vars
  abstract importPath (path: string)

  // eslint-disable-next-line no-unused-vars
  abstract open (item: MediaFileBundle)

  // eslint-disable-next-line no-unused-vars
  abstract getLocalPath (options)

  abstract hasConfig (): boolean

  // eslint-disable-next-line no-unused-vars
  abstract setConfig (key, value)

  abstract getConfig (key)

  abstract hasRestart (): boolean

  importPaths (paths) {
    for (const path of paths) {
      this.importPath(path)
    }
  }

  async importAsset (asset) {
    this.logger.info('Importing asset:', asset)

    const response = await this.api.private.locateFile({ locateEndpointRequest: { asset: asset.id } })
    if (!response.workspace) {
      this.logger.warn('Asset is outside workspaces')
      throw new Error('Asset is outside workspaces')
    }

    await this.mountWorkspaces([response.workspace])

    const exp = await this.api.mediaLibrary.editorExportXMLForAssset({ assetIds: asset.id.toString(), uri: this.uri })
    this.importXML(exp)
  }

  async querySubclipImport (bundles) {
    const assetIds = bundles.map(item => item.asset.id)
    if (!assetIds?.length) {
      return []
    }

    let importSubclips = true
    const subclips = await this.api.mediaLibrary.getAllSubclips({
      assetIn: assetIds.join(','),
    })

    if (subclips.length) {
      const text = this.translate.instant(`Import {count, plural,
        =1{one additional subclip?}
        other{{count} additional subclips?}}`, { count: subclips.length })

      try {
        await this.messageBox.show({
          text,
          positive: this.translate.instant('Yes'),
          negative: this.translate.instant('No'),
        })
      } catch {
        importSubclips = false
        this.logger.info('Not importing subclips')
      }
    }

    return importSubclips ? subclips : []
  }

  async importBundles (bundles) {
    this.logger.info('Importing bundles:', bundles)
    if (!bundles.length) {
      return
    }

    const response = await this.api.private.locateFile({ locateEndpointRequest: { path: bundles[0].mainfile.fullPath } })
    if (!response.workspace) {
      this.logger.warn('Asset is outside workspaces')
      throw new Error('Asset is outside workspaces')
    }

    await this.mountWorkspaces([response.workspace])

    const exp = await this.api.mediaLibrary.editorExportXMLForBundle({ bundleIds: bundles.map(x => x.id).join(','), uri: this.uri })
    await this.importXML(exp.contents)
  }

  async importEditorProject (fileId) {
    this.logger.info('Importing project:', fileId)

    try {
      const response = await this.api.mediaLibrary.locateEditorProjectPaths({ id: fileId })
      await this.mountWorkspaces(new Set(response.map(x => x.workspace).filter(x => x)))
    } catch (error) {
      this.logger.warn('Failed to mount workspaces:', error)
    }

    const exp = await this.api.mediaLibrary.editorExportXMLForProject({ id: fileId, uri: this.uri })
    this.importXML(exp.contents)
  }

  async mountWorkspaces (workspaceIds) {
    this.logger.debug('Automounting workspaces:', ...workspaceIds)
    const progress = this.messageBox.show({ progress: true, title: this.translate.instant('Mounting the workspace') })
    progress.catch(() => null)

    try {
      await this.elementsClient.refresh()
      if (!this.elementsClient.connected) {
        throw new Error('Client not connected')
      }
      for (const workspaceId of workspaceIds) {
        await this.elementsClient.mount(workspaceId)
      }
      progress.close()
    } catch (error) {
      progress.close()
      this.logger.error('Automount failed:', error)
    }
  }

  async importXML (xml) {
    this.logger.debug('Importing XML content')

    const fs = this.getFS()
    const path = this.getSystemPath(this.systemPath.USER_DATA) + '/.elements-temp.xml'

    try {
      await fs.writeFile(path, xml)

      this.importPath(path)
    } catch (error) {
      this.notify.error('Import failed', error)
    }
  }

  async importFile (path) {
    this.logger.info('Importing file:', path)
    const response = await this.api.private.locateFile({ locateEndpointRequest: { path } })
    if (!response.workspace) {
      this.logger.warn('Asset is outside workspaces')
      throw new Error('Asset is outside workspaces')
    }

    await this.mountWorkspaces([response.workspace])
    const url = await this.clientSideUrl.getPathUrl(path, this.platform)

    return this.importPath(url)
  }
}
